import { Component } from '@theme/component';

/**
 * @typedef {object} BookingBarRefs
 * @property {HTMLScriptElement} [data] - JSON script holding the flat list of rows.
 * @property {HTMLSelectElement} [treatment] - Treatment select.
 * @property {HTMLSelectElement} [boutique] - Boutique select.
 * @property {HTMLElement} [cta] - Wrapper around the CTA button block; holds the rendered link.
 */

/**
 * Booking bar: two dependent selects (treatment → boutique) that drive a CTA link.
 *
 * Treatment options are rendered server-side; choosing one refills the boutique select from the
 * grouped data, and choosing a boutique points the CTA at that location's booking URL. The CTA stays
 * disabled until a boutique with a URL is selected.
 *
 * @extends {Component<BookingBarRefs>}
 */
class ApcBookingBar extends Component {
  /** @type {Map<string, Array<{ boutique: string, url: string }>>} */
  #boutiquesByTreatment = new Map();

  connectedCallback() {
    super.connectedCallback();

    // The section root still renders without rows configured (onboarding state) — bail safely then.
    if (!this.refs.data || !this.refs.treatment || !this.refs.boutique || !this.refs.cta) return;

    this.#groupRows();
    this.#populateBoutiques(this.refs.treatment.value);
  }

  /**
   * Refill the boutique select for the chosen treatment.
   * Called declaratively via `on:change` on the treatment select.
   */
  onTreatmentChange = () => {
    this.#populateBoutiques(this.refs.treatment?.value ?? '');
  };

  /**
   * Update the CTA when a boutique is chosen.
   * Called declaratively via `on:change` on the boutique select.
   */
  onBoutiqueChange = () => {
    this.#updateCta();
  };

  /** Parse the JSON payload into a treatment → boutiques map, de-duplicating boutiques by name. */
  #groupRows() {
    /** @type {Array<{ treatment: string, boutique: string, url: string }>} */
    let rows = [];

    try {
      rows = JSON.parse(this.refs.data?.textContent ?? '[]');
    } catch (error) {
      console.error('apc-booking-bar: could not parse booking data', error);
      return;
    }

    for (const row of rows) {
      if (!row?.treatment || !row.boutique || !row.url) continue;

      const boutiques = this.#boutiquesByTreatment.get(row.treatment) ?? [];

      // First boutique with a given name wins (mirrors the original behaviour).
      if (!boutiques.some((entry) => entry.boutique === row.boutique)) {
        boutiques.push({ boutique: row.boutique, url: row.url });
      }

      this.#boutiquesByTreatment.set(row.treatment, boutiques);
    }
  }

  /** @param {string} treatment */
  #populateBoutiques(treatment) {
    const select = this.refs.boutique;
    if (!select) return;

    const entries = this.#boutiquesByTreatment.get(treatment) ?? [];

    // Keep the (localized) placeholder option, drop everything else.
    select.replaceChildren(this.#placeholderOption(select));

    for (const entry of entries) {
      const option = document.createElement('option');
      option.value = entry.url;
      option.textContent = entry.boutique;
      select.append(option);
    }

    select.value = '';
    select.disabled = entries.length === 0;
    this.#updateCta();
  }

  /**
   * Return the existing placeholder `<option>` (preserving its translated label) or build a new one.
   * @param {HTMLSelectElement} select
   * @returns {HTMLOptionElement}
   */
  #placeholderOption(select) {
    const existing = /** @type {HTMLOptionElement | null} */ (select.querySelector('option[value=""]'));
    if (existing) return existing;

    const option = document.createElement('option');
    option.value = '';
    return option;
  }

  #updateCta() {
    // The CTA is a button block rendered through the shared snippet, so target the link it produced.
    const link = this.refs.cta?.querySelector('a, [role="link"]');
    if (!link) return;

    const url = this.refs.boutique?.value;
    const isAnchor = link.tagName === 'A';

    if (url) {
      if (isAnchor) link.setAttribute('href', url);
      link.removeAttribute('aria-disabled');
      link.removeAttribute('tabindex');
    } else {
      if (isAnchor) link.setAttribute('href', '#');
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('tabindex', '-1');
    }
  }
}

if (!customElements.get('apc-booking-bar')) {
  customElements.define('apc-booking-bar', ApcBookingBar);
}
